import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../../axios/axiosInstance";

export const fetchApiLogs = createAsyncThunk(
  "apiLogs/fetchApiLogs",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get("/superAdmin/api-logs", { params });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch API logs");
    }
  }
);

export const fetchApiLogStats = createAsyncThunk(
  "apiLogs/fetchStats",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get("/superAdmin/api-logs/stats", { params });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const fetchApiLogTimeSeries = createAsyncThunk(
  "apiLogs/fetchTimeSeries",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get("/superAdmin/api-logs/timeseries", { params });
      return response.data.timeSeries;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const fetchDistinctRoutes = createAsyncThunk(
  "apiLogs/fetchDistinctRoutes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/superAdmin/api-logs/routes");
      return response.data.routes;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const fetchDistinctUsers = createAsyncThunk(
  "apiLogs/fetchDistinctUsers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/superAdmin/api-logs/users");
      return response.data.users;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const deleteApiLog = createAsyncThunk(
  "apiLogs/deleteApiLog",
  async (logId, { rejectWithValue }) => {
    try {
      await api.delete(`/superAdmin/api-logs/${logId}`);
      return logId;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const deleteSelectedApiLogs = createAsyncThunk(
  "apiLogs/deleteSelected",
  async (ids, { rejectWithValue }) => {
    try {
      await api.delete("/superAdmin/api-logs/batch", { data: { ids } });
      return ids;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const clearAllApiLogs = createAsyncThunk(
  "apiLogs/clearAll",
  async (_, { rejectWithValue }) => {
    try {
      await api.delete("/superAdmin/api-logs/clear");
      return true;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

const apiLogsSlice = createSlice({
  name: "apiLogs",
  initialState: {
    logs: [],
    total: 0,
    page: 1,
    limit: 50,
    distinctRoutes: [],
    distinctUsers: [],
    stats: null,
    statsLoading: false,
    timeSeries: [],
    timeSeriesLoading: false,
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchApiLogs.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchApiLogs.fulfilled, (state, action) => {
        state.loading = false;
        state.logs = action.payload.logs;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
      })
      .addCase(fetchApiLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchDistinctRoutes.fulfilled, (state, action) => {
        state.distinctRoutes = action.payload;
      })
      .addCase(fetchDistinctUsers.fulfilled, (state, action) => {
        state.distinctUsers = action.payload;
      })
      .addCase(deleteApiLog.fulfilled, (state, action) => {
        state.logs = state.logs.filter((l) => l._id !== action.payload);
        state.total = Math.max(0, state.total - 1); 
      })
      .addCase(deleteSelectedApiLogs.fulfilled, (state, action) => {
        const deleted = new Set(action.payload);
        state.logs = state.logs.filter((l) => !deleted.has(l._id));
        state.total = Math.max(0, state.total - action.payload.length);
      })

      .addCase(clearAllApiLogs.fulfilled, (state) => {
        state.logs = [];
        state.total = 0;
      })
      .addCase(fetchApiLogStats.pending, (state) => { state.statsLoading = true; })
      .addCase(fetchApiLogStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.stats = action.payload;
        // Also seed timeSeries from the initial stats load
        if (action.payload?.timeSeriesBreakdown) {
          state.timeSeries = action.payload.timeSeriesBreakdown;
        }
      })
      .addCase(fetchApiLogStats.rejected, (state) => { state.statsLoading = false; })
      .addCase(fetchApiLogTimeSeries.pending, (state) => { state.timeSeriesLoading = true; })
      .addCase(fetchApiLogTimeSeries.fulfilled, (state, action) => {
        state.timeSeriesLoading = false;
        state.timeSeries = action.payload;
      })
      .addCase(fetchApiLogTimeSeries.rejected, (state) => { state.timeSeriesLoading = false; });
  },
});

export default apiLogsSlice.reducer;
